import { Editor } from "@tiptap/react";
import { nanoid } from "nanoid";
import queryString from "query-string";
import { useRecoilState, useRecoilValue } from "recoil";
import { Instance } from "tippy.js";
import { ActiveEmailAtom } from "../atoms/ActiveEmailAtom";
import EmailSelectorFamily, { EmailMode } from "../atoms/EmailAtom";
import { UserAtom } from "../atoms/UserAtom";
import { useModal } from "../components/Modal/Modal";
import LinkPopover from "../components/NewEmail/LinkPopover";
import { selectionMarks } from "../components/NewEmail/TiptapAutoLink";
import { ITag } from "../types/TagTypes";
import {
  getAnchorByURLAndSelect,
  restoreRange,
  selectAnchorElementURL,
  showURLTooltip,
} from "../utils/LinkUtils";

export const FindLinkRegex =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(((&amp;)|(\{\{Contact\.\w+\}\})|[-a-zA-Z0-9()@:%_\+.~#?&//=])*)/;

function useLinkPopover(
  editor: Editor,
  tooltips: React.MutableRefObject<{ [href: string]: Instance }>,
  mode: EmailMode,
  index: number
) {
  const modal = useModal();
  const [email, setEmail] = useRecoilState(
    EmailSelectorFamily({ mode, index })
  );
  const user = useRecoilValue(UserAtom);

  const addInterestTag = (url: string, selectedTags: ITag[]): string => {
    let queriedURL = "";
    let positionCounter = 0;

    do {
      queriedURL = queryString
        .stringifyUrl({
          // url: interestTags.length ? interestTags[0].url : url,
          url,
          query: { __position__: positionCounter },
        })
        .replaceAll("%25", "%");

      positionCounter = positionCounter + 1;
    } while (Object.keys(email?.interestingURLs || {}).includes(queriedURL));

    setEmail({
      ...email,
      // @ts-ignore
      interestingURLs: {
        ...email.interestingURLs,
        [queriedURL]: selectedTags.map((tag) => tag._id),
      },
    });

    return queriedURL;
  };

  const restoreSelection = (
    hasAnchor: boolean,
    tooltipId?: string,
    newURL?: string,
    oldURL?: string,
    selectedText?: string,
    range?: Range
  ) => {
    if (hasAnchor) {
      if (newURL) {
        getAnchorByURLAndSelect(tooltipId);
        setTimeout(() => showURLTooltip(tooltipId, tooltips.current), 1000);
      } else {
        selectAnchorElementURL(tooltipId, tooltips.current);
      }
    } else if (selectedText) {
      restoreRange(range);
    }
    if (document.querySelector(".ProseMirror")) {
      //@ts-ignore
      document.querySelector(".ProseMirror").editor.view.focus();
    }
  };

  const onSaveDecorator =
    (
      hasAnchor: boolean,
      selectedText: string,
      range: Range,
      tooltipId?: string
    ) =>
    (url: string, text: string, selectedTags: ITag[]) => {
      modal.popModal();

      const {
        state,
        state: { schema },
      } = editor;

      const newURL = addInterestTag(url, selectedTags);

      tooltipId ||= nanoid(); // Inserting a link

      let marks = [];

      if (!hasAnchor) {
        marks = selectionMarks(state, schema);
      } else {
        marks =
          (state.selection as any)?.node?.marks?.map(({ type }) => type.name) ||
          [];
      }

      const { from } = editor.state.selection;

      editor.commands.removeURL();
      editor.commands.addURL({
        marks,
        id: newURL,
        label: text,
        colorLink: user?.emailOptions.defaultModeColorLink,
        autoBoldLinks: !!user?.emailOptions.autoBoldLinks,
        tooltipId, // Updates the link if it already exists
      });

      // If it's a new anchor link, underline it by default,
      // if it's not a new anchor link, keep the current underline setting.
      if (!hasAnchor || marks.includes("underline"))
        editor
          .chain()
          .setNodeSelection(from)
          .setUnderline({ color: user?.emailOptions.defaultModeColorLink })
          .run();
      if (marks.includes("italic"))
        editor.chain().setNodeSelection(from).setItalic().run();
      if (marks.includes("bold"))
        editor.chain().setNodeSelection(from).setBold().run();

      restoreSelection(
        true, // Inserting or updating a link
        tooltipId,
        newURL,
        undefined,
        selectedText,
        range
      );
    };

  const onCancel = (
    hasAnchor: boolean,
    oldURL: string,
    selectedText: string,
    range: Range,
    tooltipId?: string
  ) => {
    modal.popModal();

    restoreSelection(
      hasAnchor,
      tooltipId,
      undefined,
      oldURL,
      selectedText,
      range
    );
  };

  const openLinkModal = (
    hasAnchor: boolean,
    selectedText: string,
    range: Range,
    tooltipId?: string,
    linkURL?: string,
    linkLabel?: string
  ) => {
    if (!editor) return null;

    const isRawLink = FindLinkRegex.test(linkLabel || "");
    const sanitizedLinkURL = queryString
      .exclude(linkURL || "", ["__position__"])
      .trim();
    const selectedStandardTags =
      email.interestingURLs && email.interestingURLs[linkURL || ""];

    modal.pushModal(
      <LinkPopover
        selectedText={selectedText}
        onSave={onSaveDecorator(hasAnchor, selectedText, range, tooltipId)}
        onCancel={() =>
          onCancel(hasAnchor, linkURL, selectedText, range, tooltipId)
        }
        hasAnchor={hasAnchor}
        isRawLink={isRawLink}
        linkURL={sanitizedLinkURL}
        linkLabel={linkLabel}
        initialSelectedTags={selectedStandardTags}
      />,
      "Edit Link",
      undefined,
      undefined,
      () => {
        restoreSelection(
          hasAnchor,
          tooltipId,
          undefined,
          linkURL,
          selectedText,
          range
        );
      }
    );
  };

  return { openLinkModal };
}

export default useLinkPopover;
